/**
 * ErrorBoundary.tsx
 *
 * Two error boundaries:
 *   GlobalErrorBoundary — wraps the entire app shell.
 *   RouteErrorBoundary  — wraps each route; errors here don't crash the shell.
 *
 * Rules (per CLAUDE.md Error #9):
 *   - Every route is wrapped in <RouteErrorBoundary>.
 *   - The app shell is wrapped in <GlobalErrorBoundary>.
 *   - Both display meaningful error UI with a retry/reload action.
 *   - Neither swallows errors silently.
 *
 * Usage:
 *   <GlobalErrorBoundary>
 *     <Router>
 *       <Route path="/rooms" element={
 *         <RouteErrorBoundary>
 *           <RoomsPage />
 *         </RouteErrorBoundary>
 *       } />
 *     </Router>
 *   </GlobalErrorBoundary>
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError:   boolean
  error:      Error | null
  errorInfo:  ErrorInfo | null
}

interface ErrorFallbackProps {
  error:      Error | null
  onRetry:    () => void
  isGlobal?:  boolean
}

// ---------------------------------------------------------------------------
// ErrorFallback — shared fallback UI
// ---------------------------------------------------------------------------

function ErrorFallback({ error, onRetry, isGlobal = false }: ErrorFallbackProps): ReactNode {
  const { t } = useTranslation()
  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        padding:        '2rem',
        minHeight:      isGlobal ? '100vh' : '12rem',
        textAlign:      'center',
        fontFamily:     'system-ui, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth:     '36rem',
          width:        '100%',
          background:   '#fff',
          border:       '1px solid #fee2e2',
          borderRadius: '0.75rem',
          padding:      '2rem',
          boxShadow:    '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', color: '#111827' }}>
          {isGlobal ? t('error.somethingWentWrong') : t('error.sectionFailed')}
        </h2>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
          {isGlobal ? t('error.globalDetail') : t('error.routeDetail')}
        </p>

        {/* Error detail — visible to aid support without exposing a raw stack */}
        {error?.message && (
          <pre
            style={{
              textAlign:    'left',
              background:   '#fef2f2',
              border:       '1px solid #fecaca',
              borderRadius: '0.5rem',
              padding:      '0.75rem',
              fontSize:     '0.75rem',
              color:        '#b91c1c',
              overflowX:    'auto',
              marginBottom: '1.25rem',
              whiteSpace:   'pre-wrap',
              wordBreak:    'break-word',
            }}
          >
            {error.message}
          </pre>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding:      '0.5rem 1.25rem',
              background:   '#2563eb',
              color:        '#fff',
              border:       'none',
              borderRadius: '0.5rem',
              fontSize:     '0.875rem',
              fontWeight:   600,
              cursor:       'pointer',
            }}
          >
            {isGlobal ? t('error.reloadPage') : t('error.retry')}
          </button>
          {!isGlobal && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding:      '0.5rem 1.25rem',
                background:   'transparent',
                color:        '#374151',
                border:       '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize:     '0.875rem',
                fontWeight:   500,
                cursor:       'pointer',
              }}
            >
              {t('error.reloadPage')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// GlobalErrorBoundary
// ---------------------------------------------------------------------------

/**
 * Catches any error that escapes the route boundaries.
 * Clicking "Reload page" does a hard reload.
 */
export class GlobalErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Record to the audit trail via the structured error shape.
    // We cannot call async supabase here (class component), so fire-and-forget
    // via the fetch API if the env variable is available.
    const supabaseUrl = (window as unknown as Record<string, string>)['VITE_SUPABASE_URL']
    if (supabaseUrl) {
      fetch(`${supabaseUrl}/functions/v1/audit-log`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action_type:  'client.global_error',
          entity_type:  'client_error',
          metadata:     {
            message:        error.message,
            component_stack: errorInfo.componentStack?.slice(0, 500) ?? null,
          },
        }),
      }).catch(() => { /* non-critical — do not throw from error boundary */ })
    }

    this.setState({ errorInfo })
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleReload}
          isGlobal
        />
      )
    }
    return this.props.children
  }
}

// ---------------------------------------------------------------------------
// RouteErrorBoundary
// ---------------------------------------------------------------------------

/**
 * Catches errors within a single route. Clicking "Retry" unmounts and remounts
 * the child tree. Clicking "Reload page" does a hard reload.
 */
export class RouteErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(_error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          isGlobal={false}
        />
      )
    }
    return this.props.children
  }
}
